import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Button, Modal, Table, Text } from "@pentabd/ui";

import {
  ErrorModal,
  SelectControllerVertical,
  SuccessModal,
} from "@components";
import { AddTagModalTableColumn } from "../index";

import { useMetaData } from "@hooks/inventory/roomConfiguration/useMetaData";
import { useLanguage } from "@hooks/useLanguage";

import { getConvertedMetaData } from "@helpers/inventory/metaDataConvert";

import { ROOM_META_DATA } from "@constants/inventory/room-meta-data";

import { tagProduct } from "@api/inventory/productDetails";
import { ProductType, TagProductType } from "@type/inventory/procurements";

const { OFFICE, BUILDING, ROOM } = ROOM_META_DATA;

type Props = {
  procurementData: any;
  isOpen: boolean;
  onClose: () => void;
  data: ProductType;
};

type NewArrayType = {
  [key: string]: any;
  id: number;
  nameEn: string;
  nameBn: string;
  product: any;
  room: null | number;
  tagNo: null | string;
  building: null | number;
  office: null | number;
  condition: any;
  barcode: any;
  amount: number | null;
};

type TagResponse = {
  isLoading: boolean;
  error: any;
  data: any;
};

const AddTagModal = ({ procurementData, isOpen, onClose, data }: Props) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { id } = useParams();

  const [tagData, setTagData] = useState<TagResponse>({
    isLoading: false,
    error: null,
    data: null,
  });
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { handleQueryById } = procurementData;

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<TagProductType>();

  const { amount, name, id: productId, type } = data || {};

  const { data: offices } = useMetaData(OFFICE);
  const { data: buildings, setQuery: setBuildingQueries } =
    useMetaData(BUILDING);
  const { data: room, setQuery: setRoomQueries } = useMetaData(ROOM);

  let office = watch("office");
  let building = watch("building");

  const sizeOfArray = type === 1 ? amount : 1;

  let newArray: NewArrayType[] = Array.from(
    { length: sizeOfArray },
    (item, index) => {
      return {
        id: index,
        nameEn: name?.nameEn,
        nameBn: name?.nameBn,
        product: productId,
        room: null,
        tagNo: null,
        building: null,
        office: null,
        condition: null,
        barcode: null,
        amount: type === 1 ? null : amount,
      };
    },
  );

  const [taging, setTaging] = useState<NewArrayType[]>(
    Array.from({ length: sizeOfArray }, (item, index) => {
      return {
        id: index,
        nameEn: name?.nameEn,
        nameBn: name?.nameBn,
        product: productId,
        room: null,
        tagNo: null,
        building: null,
        office: null,
        condition: null,
        barcode: null,
        amount: type === 1 ? null : amount,
      };
    }),
  );

  const onSubmit: SubmitHandler<TagProductType> = async (data) => {
    const _newArray = newArray.map((item) => {
      return {
        ...item,
        building: data.building,
        office: data.office,
        room: item.room ? Number(item.room) : null,
      };
    });

    setTagData({
      isLoading: true,
      error: null,
      data: null,
    });

    const res = await tagProduct(productId || "", _newArray);

    setTagData({
      isLoading: false,
      error: res.error,
      data: res.data,
    });
    if (res.data) {
      setIsSuccessModalOpen(true);
    }
    if (res.error) {
      setIsErrorModalOpen(true);
    }
  };

  const createSuccessAction = () => {
    onClose();
    handleQueryById(id || "");
    setIsSuccessModalOpen(false);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const setData = (
    id: number | string,
    value: number | string,
    name: string,
  ) => {
    const foundObject = newArray.find((obj) => obj.id === id);
    if (foundObject) {
      foundObject[name] = value;
    }

    setTaging((prev: NewArrayType[]) => {
      const foundObject = prev.find((obj: NewArrayType) => obj.id === id);
      if (foundObject) {
        foundObject[name] = value;
      }
      return prev;
    });
  };

  useEffect(() => {
    if (office) {
      setBuildingQueries({
        officeId: office,
      });
    }
  }, [office, setBuildingQueries]);

  useEffect(() => {
    if (building) {
      setRoomQueries({
        buildingId: building,
      });
    }
  }, [building, setRoomQueries]);

  return (
    <div>
      <Modal isOpen={isOpen} onClose={onClose} overlay>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-10 modal_width">
            <div className="border-bottom pb-10 mb-10">
              <Text component="h3" size="md">
                {t("PROCUREMENTS_DETAILS.ADD_TAG_MODAL.TITLE")}
              </Text>
              <Text component="p" weight="light" size="sm" className="mt-1">
                {t("PROCUREMENTS_DETAILS.ADD_TAG_MODAL.SUB_TITLE")}
              </Text>
            </div>
            <div className="mb-10">
              <div className="mt-5">
                <SelectControllerVertical
                  label="PROCUREMENTS_DETAILS.ADD_TAG_MODAL.FORM.FIELD_1"
                  control={control}
                  name="office"
                  register={{
                    ...register("office", {
                      required: "Office Location is required",
                    }),
                  }}
                  options={getConvertedMetaData(offices, language)}
                  setValue={setValue}
                  error={errors}
                  required
                />
              </div>
              <div className="mt-5">
                <SelectControllerVertical
                  label="PROCUREMENTS_DETAILS.ADD_TAG_MODAL.FORM.FIELD_2"
                  control={control}
                  name="building"
                  register={{
                    ...register("building", {
                      required: "Building Location is required",
                    }),
                  }}
                  options={getConvertedMetaData(buildings, language)}
                  setValue={setValue}
                  error={errors}
                  required
                />
              </div>
            </div>

            <div className="addTagTableWrapper">
              <Table
                columns={AddTagModalTableColumn(t, room, setData, type) as any}
                rows={newArray}
              />
            </div>

            {/* <>
              {JSON.stringify(taging.map((f) => f.room))}
              {JSON.stringify(
                taging.reduce((acc, curr) => acc && !!curr.room, true)
              )}
            </> */}
            {/* {taging.reduce((acc, curr) => acc && !!curr.room, true) && ( */}
            <div className="mt-5 col-12 text-end">
              <Button
                fill="outline"
                type="primary"
                size="xs"
                className="me-5"
                onClick={onClose}
              >
                {t(
                  "PROCUREMENTS_DETAILS.ADD_TAG_MODAL.CANCEL_BUTTON.BUTTON_TEXT",
                )}
              </Button>
              <Button
                fill="fill"
                type="primary"
                size="xs"
                htmlType="submit"
                loading={tagData.isLoading}
              >
                {t(
                  "PROCUREMENTS_DETAILS.ADD_TAG_MODAL.SAVE_BUTTON.BUTTON_TEXT",
                )}
              </Button>
            </div>
            {/* )} */}
          </div>
        </form>
      </Modal>
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
    </div>
  );
};

export default AddTagModal;
